import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import Chip from '@mui/joy/Chip';
import LinearProgress from '@mui/joy/LinearProgress';

import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ImageIcon from '@mui/icons-material/Image';

import ImageUpload from '../forms/ImageUpload';
import UrlNameInput from '../forms/UrlNameInput';

import { themeColors, shadows } from '../../styles/theme';

const STEPS = [
  {
    id: 'basic',
    label: 'Basic Information',
    description: 'Name your workspace and set the URL',
    icon: WorkspacesIcon
  },
  {
    id: 'branding',
    label: 'Branding',
    description: 'Add logo and description',
    icon: ImageIcon
  },
  {
    id: 'review',
    label: 'Review & Create',
    description: 'Confirm your settings',
    icon: CheckCircleIcon
  }
];

export default function ModernCreateWorkspaceModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  error = null
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    url_name: '',
    description: '',
    logo: null
  });
  const [urlError, setUrlError] = useState('');

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'url_name') {
      setUrlError(!value ? 'URL name is required' : '');
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.name || !formData.url_name) return;
    }

    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(formData);
    handleReset();
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      name: '',
      url_name: '',
      description: '',
      logo: null
    });
    setUrlError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return formData.name && formData.url_name && !urlError;
      case 1:
        return true;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const progress = ((activeStep + 1) / STEPS.length) * 100;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <FormControl required>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Workspace Name
              </FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g., Acme Corporation"
                autoFocus
                sx={{
                  border: 'none',
                  boxShadow: shadows.sm,
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '--Input-focusedThickness': '0px',
                  '&:focus-within': {
                    boxShadow: shadows.focus,
                  },
                  transition: 'all 0.2s ease',
                  fontSize: '1rem',
                  py: 1.5,
                }}
              />
            </FormControl>

            <UrlNameInput
              value={formData.url_name}
              onChange={(value) => updateField('url_name', value)}
              label="URL Name"
              required
              error={urlError}
              placeholder="acme-corporation"
              baseUrl={window.location.origin + '/client/'}
              helperText="This will be part of your workspace's URL"
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <ImageUpload
              value={formData.logo}
              onChange={(file) => updateField('logo', file)}
              label="Workspace Logo"
              previewSize={120}
              helperText="Optional: Upload a logo for your workspace branding"
            />

            <FormControl>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Description
                <Typography component="span" level="body-sm" sx={{ ml: 1, color: themeColors.neutral[500], fontWeight: 400 }}>
                  (Optional)
                </Typography>
              </FormLabel>
              <Textarea
                minRows={3}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe your workspace and what it's used for..."
                sx={{
                  border: 'none',
                  boxShadow: shadows.sm,
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '--Textarea-focusedThickness': '0px',
                  '&:focus-within': {
                    boxShadow: shadows.focus,
                  },
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                }}
              />
            </FormControl>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 4,
                backgroundColor: 'white',
                borderRadius: 2.5,
                border: 'none',
                boxShadow: shadows.md,
              }}
            >
              <Stack spacing={3}>
                {/* Logo and Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {formData.logo ? (
                    <img
                      src={URL.createObjectURL(formData.logo)}
                      alt="Logo preview"
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 12,
                        objectFit: 'cover',
                        boxShadow: shadows.md,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2.5,
                        background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 16px ${themeColors.primary[500]}30`,
                      }}
                    >
                      <Typography level="h3" sx={{ color: 'white', fontWeight: 700 }}>
                        {formData.name[0]?.toUpperCase() || 'W'}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 700 }}>
                      Workspace Name
                    </Typography>
                    <Typography level="h4" sx={{ color: themeColors.primary[500], fontWeight: 700 }}>
                      {formData.name}
                    </Typography>
                  </Box>
                </Box>

                {/* URL */}
                <Box>
                  <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontWeight: 700 }}>
                    URL Path
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: themeColors.neutral[100],
                      px: 2,
                      py: 1,
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography sx={{ fontFamily: 'monospace', color: themeColors.neutral[700], fontWeight: 600 }}>
                      /client/{formData.url_name}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                {formData.description && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontWeight: 700 }}>
                      Description
                    </Typography>
                    <Typography level="body-md" sx={{ color: themeColors.neutral[700] }}>
                      {formData.description}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Alert
              variant="soft"
              color="success"
              sx={{
                borderRadius: 2,
                border: 'none',
                backgroundColor: `${themeColors.success}10`,
              }}
            >
              <Typography level="body-sm">
                Everything looks good! Click "Create Workspace" to get started.
              </Typography>
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  const StepIcon = STEPS[activeStep].icon;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: 650,
          maxHeight: '90vh',
          overflow: 'auto',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 3,
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            background: `linear-gradient(135deg, ${themeColors.primary[500]}08, ${themeColors.primary[500]}03)`,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            position: 'relative',
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              backgroundColor: 'white',
              boxShadow: shadows.sm,
              '&:hover': {
                backgroundColor: themeColors.neutral[100],
              },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 16px ${themeColors.primary[500]}30`,
              }}
            >
              <StepIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography level="h3" sx={{ mb: 0.5, fontWeight: 700, color: themeColors.neutral[900] }}>
                {STEPS[activeStep].label}
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                {STEPS[activeStep].description}
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600], fontWeight: 600 }}>
                Step {activeStep + 1} of {STEPS.length}
              </Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600], fontWeight: 600 }}>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progress}
              sx={{
                backgroundColor: themeColors.neutral[200],
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                },
                height: 6,
                borderRadius: 3,
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          {error && (
            <Alert
              color="danger"
              sx={{
                mb: 3,
                borderRadius: 2,
                border: 'none',
                boxShadow: shadows.sm,
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ minHeight: 280, mb: 4 }}>
              {renderStepContent()}
            </Box>

            {/* Footer Actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                pt: 3,
                borderTop: `1px solid ${themeColors.neutral[100]}`,
              }}
            >
              <Button
                variant="plain"
                onClick={handleBack}
                disabled={activeStep === 0}
                startDecorator={<ArrowBackIcon />}
                sx={{
                  visibility: activeStep === 0 ? 'hidden' : 'visible',
                  color: themeColors.neutral[700],
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeStep === STEPS.length - 1 ? (
                  <Button
                    type="submit"
                    loading={loading}
                    disabled={!canProceed()}
                    startDecorator={<CheckCircleIcon />}
                    size="lg"
                    sx={{
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                      boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                      fontWeight: 600,
                      px: 4,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Create Workspace
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    endDecorator={<ArrowForwardIcon />}
                    size="lg"
                    sx={{
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                      boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                      fontWeight: 600,
                      px: 4,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
